/**
 * Overlay + Expand/Pin chrome for operator Messages and Smoke Drive windows.
 * Pinned windows release the exclusive overlay slot so both can stay open.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useOverlayManager } from '../../context/OverlayManagerContext';
import { useOperatorWorkspacePinnedDrag } from './useOperatorWorkspacePinnedDrag';

let workspaceZ = 10051;

export function bumpOperatorWorkspaceZ() {
    workspaceZ += 1;
    return workspaceZ;
}

/**
 * @param {{
 *   overlayId: string,
 *   readChrome: () => { expanded: boolean, pinned: boolean, leftPct: number|null, topPct: number|null },
 *   writeChrome: (patch: object) => void,
 *   readPinnedPosition: () => { leftPct: number, topPct: number }|null,
 *   writePinnedPosition: (pos: { leftPct: number, topPct: number }|null) => { leftPct: number, topPct: number }|null,
 * }} opts
 */
export function useOperatorWorkspaceShell({
    overlayId,
    readChrome,
    writeChrome,
    readPinnedPosition,
    writePinnedPosition,
}) {
    const { activeOverlay, closeOverlay, openOverlay } = useOverlayManager();
    const overlayActive = activeOverlay === overlayId;
    const [panelOpen, setPanelOpen] = useState(false);
    const needsOverlayClaimRef = useRef(false);
    const modalRef = useRef(null);
    const [chrome, setChrome] = useState(() => readChrome());
    const [zIndex, setZIndex] = useState(null);

    const expanded = Boolean(chrome.expanded);
    const pinned = Boolean(chrome.pinned);

    const close = useCallback(() => {
        setPanelOpen(false);
        needsOverlayClaimRef.current = false;
        closeOverlay(overlayId);
    }, [closeOverlay, overlayId]);

    const updateChrome = useCallback(
        (patch) => {
            setChrome((prev) => {
                const next = {
                    ...prev,
                    expanded: patch.expanded != null ? Boolean(patch.expanded) : prev.expanded,
                    pinned: patch.pinned != null ? Boolean(patch.pinned) : prev.pinned,
                };
                if (patch.leftPct !== undefined) next.leftPct = patch.leftPct;
                if (patch.topPct !== undefined) next.topPct = patch.topPct;
                writeChrome(patch);
                return next;
            });
        },
        [writeChrome]
    );

    const toggleExpand = useCallback(() => {
        updateChrome({ expanded: !expanded });
    }, [expanded, updateChrome]);

    const togglePin = useCallback(() => {
        if (pinned) {
            updateChrome({ pinned: false });
            if (panelOpen) {
                needsOverlayClaimRef.current = true;
                openOverlay(overlayId);
            }
            return;
        }
        updateChrome({ pinned: true });
    }, [pinned, panelOpen, openOverlay, overlayId, updateChrome]);

    const bringToFront = useCallback(() => {
        setZIndex(bumpOperatorWorkspaceZ());
    }, []);

    const {
        hasCustomPosition,
        moving: shellMoving,
        onHandlePointerDown,
    } = useOperatorWorkspacePinnedDrag({
        enabled: panelOpen && pinned,
        shellRef: modalRef,
        readPinnedPosition,
        writePinnedPosition,
    });

    useEffect(() => {
        if (overlayActive) {
            needsOverlayClaimRef.current = false;
            setPanelOpen(true);
            return;
        }
        if (pinned && panelOpen) return;
        if (needsOverlayClaimRef.current) return;
        setPanelOpen(false);
    }, [overlayActive, pinned, panelOpen]);

    useEffect(() => {
        if (!panelOpen || !pinned) return;
        needsOverlayClaimRef.current = false;
        closeOverlay(overlayId);
        const ae = document.activeElement;
        if (ae && modalRef.current?.contains(ae) && typeof ae.blur === 'function') {
            ae.blur();
        }
    }, [panelOpen, pinned, closeOverlay, overlayId]);

    useEffect(() => {
        if (panelOpen) bringToFront();
    }, [panelOpen, bringToFront]);

    useEffect(() => {
        if (!panelOpen) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (pinned) return;
            if (
                e.target?.tagName === 'INPUT' ||
                e.target?.tagName === 'TEXTAREA' ||
                e.target?.tagName === 'SELECT' ||
                e.target?.isContentEditable
            ) {
                return;
            }
            close();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [panelOpen, pinned, close]);

    return {
        panelOpen,
        expanded,
        pinned,
        modalRef,
        close,
        toggleExpand,
        togglePin,
        bringToFront,
        zIndex,
        hasCustomPosition,
        shellMoving,
        onHandlePointerDown,
        closeOverlay,
    };
}
